import { useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useService, useChecks, useHourlyStats, useTriggerCheck, useDeleteService, useRotatePushToken } from '../../hooks/useServices';
import { useIncidents, useResolveIncident, useAcknowledgeIncident } from '../../hooks/useIncidents';
import { StatusBadge } from '../common/StatusBadge';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { DetailSkeleton, Skeleton } from '../common/Skeleton';
import { Tabs, TabItem, tabPanelProps } from '../common/Tabs';
import { IncidentRow } from '../incidents/IncidentRow';
import { ResponseTimeChart } from './ResponseTimeChart';
import { UptimeChart } from './UptimeChart';
import { EstimateBadge } from '../reports/EstimateBadge';
import { useToast } from '../common/Toast';
import { useAuth } from '../../context/AuthContext';
import { apiError } from '../../utils/apiError';
import { displayUrl, formatDuration, formatRelativeTime, formatUptime, statusLabels, typeLabels } from '../../utils/status';
import { Diagnostics } from './Diagnostics';
import { ServiceMoreMenu } from './ServiceMoreMenu';
import { describeAssertion } from './AssertionsEditor';
import { Check } from '../../types';
import { formatDateTime, parseServerDate } from '../../utils/datetime';
import { selectResponseSeries, windowBounds } from '../../utils/series';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const TABS: TabItem[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'diagnostics', label: 'Diagnostics' },
  { key: 'incidents', label: 'Incidents' },
  { key: 'history', label: 'History' },
  { key: 'settings', label: 'Settings' },
];

/** Windows offered for the overview charts. */
const RANGES = [
  { hours: 1, label: '1h' },
  { hours: 24, label: '24h' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
];

/**
 * Raw checks pulled for the response chart. A 30-day window at a 30s interval
 * is ~86k checks; this covers 24h at the default 60s interval, and past it
 * the panel switches to the hourly rollup (see selectResponseSeries).
 */
const RAW_SAMPLE_CAP = 1500;

export function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const serviceId = parseInt(id!, 10);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') ?? 'overview';
  const tab = TABS.some(t => t.key === requestedTab) ? requestedTab : 'overview';
  const rangeHours = Number(searchParams.get('range')) || 24;

  const setParam = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  };

  const { data: service, isLoading } = useService(serviceId);
  const { data: checksData } = useChecks(serviceId, RAW_SAMPLE_CAP, rangeHours);
  const { data: recentChecks } = useChecks(serviceId, 100);
  const { data: hourlyStats } = useHourlyStats(serviceId, Math.max(rangeHours, 24));
  const { data: incidents, isLoading: incidentsLoading } = useIncidents({ service_id: serviceId, limit: 100 });
  const resolveIncident = useResolveIncident();
  const acknowledgeIncident = useAcknowledgeIncident();
  const triggerCheck = useTriggerCheck();
  const deleteService = useDeleteService();
  const rotateToken = useRotatePushToken();

  useDocumentTitle(service?.name ?? 'Service');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState<number | null>(null);

  if (isLoading) return <DetailSkeleton />;

  if (!service) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">That service no longer exists.</p>
        <Link to="/" className="text-brand-600 dark:text-brand-400 hover:underline mt-2 inline-block">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const handleTrigger = () => {
    triggerCheck.mutate(serviceId, {
      onSuccess: () => addToast('success', 'Check triggered'),
      onError: err => addToast('error', apiError(err, 'Failed to trigger check')),
    });
  };

  const handleDelete = () => {
    deleteService.mutate(serviceId, {
      onSuccess: () => {
        addToast('success', `Deleted "${service.name}"`);
        setConfirmDelete(false);
        navigate('/');
      },
      onError: err => {
        addToast('error', apiError(err, 'Failed to delete service'));
        setConfirmDelete(false);
      },
    });
  };

  const rangeChecks = checksData?.data ?? [];
  // `total` is the window's true count; `rangeChecks` is the capped sample.
  const checksInWindow = checksData?.total ?? rangeChecks.length;
  const allChecks = recentChecks?.data ?? [];
  const historyChecks = failuresOnly ? allChecks.filter(c => c.status !== 'up') : allChecks;
  const openIncident = incidents?.find(i => i.ongoing);
  const responseSeries = selectResponseSeries({
    checks: rangeChecks,
    totalInWindow: checksInWindow,
    sampleCap: RAW_SAMPLE_CAP,
    intervalSeconds: service.interval,
    rangeHours,
    hourlyStats: hourlyStats ?? [],
  });
  // One window for both panels so their axes line up exactly. The end is
  // `now`, or the newest check if the client clock trails the server's --
  // the axis clips to its domain, and the freshest point must never be the
  // thing that gets clipped. rangeChecks is newest-first from the server.
  const newestCheck = parseServerDate(rangeChecks[0]?.timestamp)?.getTime() ?? 0;
  const chartWindow = windowBounds(rangeHours, Math.max(Date.now(), newestCheck));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight">{service.name}</h1>
            <StatusBadge status={service.status} />
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 uppercase tracking-wide" title="Check type">
              {typeLabels[service.type] ?? service.type}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-mono break-all">
            {service.type === 'heartbeat'
              ? `expects a ping every ${service.interval}s (+${service.heartbeat_grace}s grace)`
              : service.type === 'group'
                ? `${service.group_mode === 'any' ? 'up when any' : 'up when all'} of ${service.children?.length ?? 0} children`
                : service.type === 'http' ? service.url : displayUrl(service.url, service.type)}
          </p>
          {service.tags?.length > 0 && (
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {service.tags.map(t => (
                <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          <Button variant="secondary" onClick={handleTrigger} loading={triggerCheck.isPending}>
            {triggerCheck.isPending ? 'Checking…' : 'Check now'}
          </Button>
          <Link to={{ pathname: `/reports/services/${serviceId}`, search: location.search }}>
            <Button variant="secondary">Scorecard</Button>
          </Link>
          <Link to={`/services/${serviceId}/edit`}>
            <Button variant="secondary">Edit</Button>
          </Link>
          <ServiceMoreMenu service={service} rangeHours={rangeHours} />
          <Button variant="ghost" onClick={() => setConfirmDelete(true)}>Delete</Button>
        </div>
      </div>

      {service.status === 'maintenance' && (
        <div
          className="rounded-lg border border-brand-200 dark:border-brand-900 bg-brand-50 dark:bg-brand-900/20 px-4 py-3 flex items-start justify-between gap-4 flex-wrap"
          role="status"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-brand-900 dark:text-brand-200">In maintenance</p>
            <p className="text-sm text-brand-800 dark:text-brand-300 mt-0.5">
              Checks continue but no alerts are sent, and this time is left out of uptime.
              {service.last_check_status && service.last_check_status !== 'up' && (
                <> The last check was <span className="font-medium">{statusLabels[service.last_check_status].toLowerCase()}</span>.</>
              )}
            </p>
          </div>
          <Link to="/maintenance" className="shrink-0">
            <Button size="sm" variant="secondary">Maintenance windows</Button>
          </Link>
        </div>
      )}

      {service.status !== 'maintenance' && service.last_check_status && service.last_check_status !== service.status && service.status !== 'pending' && (
        <p className="text-sm text-gray-600 dark:text-gray-400" role="status">
          Last check was <span className="font-medium">{statusLabels[service.last_check_status].toLowerCase()}</span>
          {' '}— {service.status === 'up'
            ? `${service.consecutive_failures} of ${service.down_after} failures needed to confirm an outage.`
            : `${service.consecutive_successes} of ${service.up_after} passes needed to confirm recovery.`}
        </p>
      )}

      {service.type === 'heartbeat' && service.push_url && (
        <Card className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h2 className="font-semibold">Push URL</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Have the job request this when it finishes. Add <span className="font-mono">/start</span> at the beginning to record its duration,
                or <span className="font-mono">/fail</span> instead of the plain ping to report a failure at once.
              </p>
            </div>
            {hasPermission('services.edit') && (
              <Button size="sm" variant="secondary" onClick={() => setConfirmRotate(true)} loading={rotateToken.isPending}>
                Rotate token
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-900 font-mono text-xs break-all">{service.push_url}</code>
            <CopyButton text={service.push_url} />
          </div>
          <pre className="font-mono text-xs p-3 rounded-md bg-gray-50 dark:bg-gray-900 overflow-x-auto">{`# crontab
0 3 * * * /usr/local/bin/backup.sh && curl -fsS -m 10 --retry 3 ${service.push_url} > /dev/null

# with duration and failure reporting
curl -fsS ${service.push_url}/start
/usr/local/bin/backup.sh && curl -fsS ${service.push_url} || curl -fsS ${service.push_url}/fail`}</pre>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Last ping</dt>
              <dd className="font-medium">{service.last_heartbeat_at ? formatRelativeTime(service.last_heartbeat_at) : 'never'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Next deadline</dt>
              <dd className="font-medium">{service.heartbeat_deadline_at ? formatDateTime(service.heartbeat_deadline_at) : '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Last run took</dt>
              <dd className="font-medium font-mono">{service.heartbeat_duration_ms !== null && service.heartbeat_duration_ms !== undefined ? formatDuration(service.heartbeat_duration_ms) : '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Last signal</dt>
              <dd className="font-medium">{service.heartbeat_last_signal === 'fail' ? 'failure reported' : service.heartbeat_last_signal === 'ok' ? 'ok' : '—'}</dd>
            </div>
          </dl>
        </Card>
      )}

      {service.type === 'group' && service.children && (
        <Card className="p-4">
          <h2 className="font-semibold mb-2">Children</h2>
          {service.children.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">The linked group has no other enabled services yet.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {service.children.map(c => (
                <li key={c.id} className="py-2 flex items-center justify-between gap-3">
                  <Link to={`/services/${c.id}`} className="text-sm hover:underline truncate">{c.name}</Link>
                  <StatusBadge status={c.status} size="sm" />
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {openIncident && (
        <div
          className={`rounded-lg border px-4 py-3 flex items-start justify-between gap-4 flex-wrap ${
            openIncident.acknowledged_at
              ? 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20'
              : 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20'
          }`}
          role="status"
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium ${
              openIncident.acknowledged_at
                ? 'text-amber-900 dark:text-amber-200'
                : 'text-red-800 dark:text-red-300'
            }`}>
              {openIncident.acknowledged_at ? 'Acknowledged incident' : 'Open incident'} since{' '}
              {formatDateTime(openIncident.started_at)}
            </p>
            <p className={`text-sm mt-0.5 ${
              openIncident.acknowledged_at
                ? 'text-amber-800 dark:text-amber-300'
                : 'text-red-700 dark:text-red-400'
            }`}>
              {openIncident.message}
            </p>
            {openIncident.acknowledged_by_name && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Acknowledged by {openIncident.acknowledged_by_name}
              </p>
            )}
          </div>

          {hasPermission('services.edit') && (
            <div className="flex gap-2 shrink-0">
              {!openIncident.acknowledged_at && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={acknowledgeIncident.isPending}
                  onClick={() => acknowledgeIncident.mutate(
                    { id: openIncident.id },
                    {
                      onSuccess: () => addToast('success', 'Incident acknowledged'),
                      onError: err => addToast('error', apiError(err, 'Could not acknowledge')),
                    },
                  )}
                >
                  Acknowledge
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                loading={resolveIncident.isPending}
                onClick={() => resolveIncident.mutate(openIncident.id, {
                  onSuccess: () => addToast('success', 'Incident closed'),
                  onError: err => addToast('error', apiError(err, 'Could not close incident')),
                })}
              >
                Close incident
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Uptime · 24h" value={formatUptime(service.uptime_24h)} />
        <Stat label="Uptime · 7d" value={formatUptime(service.uptime_7d)} />
        <Stat label="Uptime · 30d" value={formatUptime(service.uptime_30d)} />
        <Stat label="Avg response" value={formatDuration(service.avg_response_time ?? null)} />
      </div>

      <Tabs tabs={TABS} value={tab} onChange={k => setParam('tab', k)} idBase={`service-${serviceId}`} />

      {tab === 'overview' && (
        <div className="space-y-6" {...tabPanelProps(`service-${serviceId}`, 'overview')}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {checksInWindow} checks in this window
            </p>
            <div className="flex gap-1" role="group" aria-label="Chart time range">
              {RANGES.map(r => (
                <Button
                  key={r.hours}
                  size="sm"
                  variant={rangeHours === r.hours ? 'primary' : 'secondary'}
                  aria-pressed={rangeHours === r.hours}
                  onClick={() => setParam('range', String(r.hours))}
                >
                  {r.label}
                </Button>
              ))}
            </div>
          </div>

          <Card className="p-4">
            <h2 className="text-sm font-semibold mb-4">
              Response time{responseSeries.source === 'hourly' && <EstimateBadge kind="hourly-mean" />}
            </h2>
            <ResponseTimeChart data={responseSeries.points} source={responseSeries.source} timeWindow={chartWindow} />
          </Card>
          <Card className="p-4">
            <h2 className="text-sm font-semibold mb-4">Hourly uptime</h2>
            <UptimeChart hourlyStats={hourlyStats || []} timeWindow={chartWindow} />
          </Card>
        </div>
      )}

      {tab === 'diagnostics' && (
        <div className="space-y-6" {...tabPanelProps(`service-${serviceId}`, 'diagnostics')}>
          <Card className="p-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
              <h2 className="text-sm font-semibold">Last check</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {service.last_checked_at ? formatRelativeTime(service.last_checked_at) : 'never'}
                {service.last_check_status && <> · raw result <span className="font-medium">{statusLabels[service.last_check_status].toLowerCase()}</span></>}
              </span>
            </div>
            {service.last_check_details
              ? <Diagnostics details={service.last_check_details} />
              : <p className="text-sm text-gray-500 dark:text-gray-400">No check has run yet.</p>}
          </Card>

          {(service.cert_expires_at || service.cert_issuer) && (
            <Card className="p-4">
              <h2 className="text-sm font-semibold mb-3">TLS certificate</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <Detail term="Subject" value={service.cert_subject ?? '—'} mono />
                <Detail term="Issuer" value={service.cert_issuer ?? '—'} />
                <Detail
                  term="Expires"
                  value={service.cert_expires_at ? `${formatDateTime(service.cert_expires_at)} (${certDays(service.cert_expires_at)})` : '—'}
                  mono
                />
              </dl>
            </Card>
          )}

          <Card className="p-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
              <h2 className="text-sm font-semibold">Last failure</h2>
              {service.last_error && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(service.last_error.timestamp)}</span>
              )}
            </div>
            {service.last_error ? (
              <div className="space-y-3">
                <p className="text-sm text-status-down">{service.last_error.error_message}</p>
                {service.last_error.details && <Diagnostics details={service.last_error.details} />}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No failed check on record.</p>
            )}
          </Card>
        </div>
      )}

      {tab === 'incidents' && (
        <Card className="divide-y divide-gray-200 dark:divide-gray-700" {...tabPanelProps(`service-${serviceId}`, 'incidents')}>
          {incidentsLoading ? (
            <div className="p-4 space-y-3" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !incidents?.length ? (
            <p className="p-8 text-center text-gray-500 dark:text-gray-400">
              This service has never left a healthy state.
            </p>
          ) : (
            incidents.map(i => <IncidentRow key={i.id} incident={i} showService={false} />)
          )}
        </Card>
      )}

      {tab === 'history' && (
        <div className="space-y-3" {...tabPanelProps(`service-${serviceId}`, 'history')}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last checked {service.last_checked_at ? formatRelativeTime(service.last_checked_at) : 'never'}
            </p>
            <Button
              size="sm"
              variant={failuresOnly ? 'primary' : 'secondary'}
              onClick={() => setFailuresOnly(v => !v)}
              aria-pressed={failuresOnly}
            >
              Failures only
            </Button>
          </div>

          <Card className="divide-y divide-gray-200 dark:divide-gray-700">
            {historyChecks.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                {failuresOnly ? 'No failures in the last 100 checks.' : 'No check history yet.'}
              </p>
            ) : (
              historyChecks.map(c => (
                <div key={c.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusBadge status={c.status} size="sm" />
                    <span className="text-gray-500 dark:text-gray-400 font-mono text-xs">
                      {checkLabel(service.type, c)}
                    </span>
                    {c.details && (
                      <button
                        type="button"
                        className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                        onClick={() => setExpandedCheck(expandedCheck === c.id ? null : c.id)}
                        aria-expanded={expandedCheck === c.id}
                      >
                        {expandedCheck === c.id ? 'hide details' : 'details'}
                      </button>
                    )}
                    {c.attempt > 1 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" title="Quick re-check after a failure">
                        retry {c.attempt - 1}
                      </span>
                    )}
                    {c.in_maintenance && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300" title="Checked during a maintenance window; not alerted or counted">
                        maintenance
                      </span>
                    )}
                    {c.error_message && (
                      <span className="text-status-down text-xs truncate" title={c.error_message}>
                        {c.error_message}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 font-mono text-xs tabular-nums">
                    <span>{formatDuration(c.response_time)}</span>
                    <span>{formatDateTime(c.timestamp)}</span>
                  </div>
                </div>
                {expandedCheck === c.id && c.details && (
                  <div className="mt-3 pl-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                    <Diagnostics details={c.details} compact />
                  </div>
                )}
                </div>
              ))
            )}
          </Card>
        </div>
      )}

      {tab === 'settings' && (
        <Card className="p-4" {...tabPanelProps(`service-${serviceId}`, 'settings')}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Detail term="Type" value={typeLabels[service.type] ?? service.type} />
            {service.type === 'http' && <Detail term="Method" value={service.method} mono />}
            {(service.type === 'tcp' || service.type === 'ping' || service.type === 'dns') && <Detail term="Host" value={service.host ?? '—'} mono />}
            {service.type === 'tcp' && <Detail term="Port" value={String(service.port ?? '—')} mono />}
            {service.type === 'dns' && <Detail term="Record" value={`${service.dns_record_type ?? 'A'}${service.dns_expected ? ` = ${service.dns_expected}` : ' (any answer)'}`} mono />}
            {service.type === 'group' && <Detail term="Mode" value={service.group_mode === 'any' ? 'Up when any child is up' : 'Up when all children are up'} />}
            <Detail term={service.type === 'heartbeat' ? 'Expected every' : 'Interval'} value={`${service.interval}s`} mono />
            {service.type === 'heartbeat' && <Detail term="Grace" value={`${service.heartbeat_grace}s`} mono />}
            {service.type !== 'heartbeat' && service.type !== 'group' && <Detail term="Timeout" value={`${service.timeout}ms`} mono />}
            {service.type === 'http' && <Detail term="Auth type" value={service.auth_type || 'None'} />}
            {service.type === 'http' && <Detail term="Redirects" value={service.follow_redirects ? `Follow up to ${service.max_redirects}` : 'Do not follow'} />}
            {service.type === 'http' && <Detail term="Verify TLS" value={service.tls_verify ? 'Yes' : 'No (self-signed accepted)'} />}
            {service.type === 'http' && <Detail term="Client certificate" value={service.client_cert ? 'Configured' : 'None'} />}
            {service.type === 'http' && (
              <Detail
                term="Certificate warning"
                value={service.cert_expiry_days === null ? 'Server default' : service.cert_expiry_days === 0 ? 'Off' : `${service.cert_expiry_days} days before expiry`}
              />
            )}
            {(service.resolver || service.ip_version) && (
              <Detail term="Resolution" value={[service.resolver ? `via ${service.resolver}` : null, service.ip_version ? `IPv${service.ip_version} only` : null].filter(Boolean).join(', ')} mono />
            )}
            <Detail term="Enabled" value={service.enabled ? 'Yes' : 'No'} />
            <Detail term="Quick retries" value={`${service.retries} × ${service.retry_interval}s`} mono />
            <Detail term="Confirm down / up after" value={`${service.down_after} / ${service.up_after} checks`} mono />
            <Detail
              term="Degraded above"
              value={service.degraded_threshold_ms === null ? 'Server default'
                : service.degraded_threshold_ms === 0 ? 'Off'
                  : `${service.degraded_threshold_ms}ms`}
              mono={service.degraded_threshold_ms !== null && service.degraded_threshold_ms !== 0}
            />
            {service.next_check_at && (
              <Detail term="Next check" value={formatDateTime(service.next_check_at)} mono />
            )}
            {service.type === 'http' && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Assertions</dt>
                <dd className="mt-1">
                  {service.assertions?.length ? (
                    <ul className="font-mono text-xs space-y-0.5">
                      {service.assertions.map((a, i) => <li key={i}>{describeAssertion(a)}</li>)}
                    </ul>
                  ) : <span className="text-xs text-gray-500">none — any response is up</span>}
                </dd>
              </div>
            )}
            {service.headers && Object.keys(service.headers).length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Headers</dt>
                <dd className="font-mono text-xs mt-1 p-2 bg-gray-50 dark:bg-gray-900 rounded overflow-x-auto">
                  <pre>{JSON.stringify(service.headers, null, 2)}</pre>
                </dd>
              </div>
            )}
            {service.body && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Body</dt>
                <dd className="font-mono text-xs mt-1 p-2 bg-gray-50 dark:bg-gray-900 rounded whitespace-pre-wrap break-all">
                  {service.body}
                </dd>
              </div>
            )}
          </dl>
        </Card>
      )}

      <ConfirmDialog
        open={confirmRotate}
        title="Rotate push token"
        message="The current push URL stops working immediately. Every job that pings it must be updated to the new URL."
        confirmLabel="Rotate token"
        loading={rotateToken.isPending}
        onConfirm={() => rotateToken.mutate(serviceId, {
          onSuccess: () => { addToast('success', 'Push token rotated'); setConfirmRotate(false); },
          onError: err => { addToast('error', apiError(err, 'Could not rotate the token')); setConfirmRotate(false); },
        })}
        onClose={() => setConfirmRotate(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete service"
        message={`"${service.name}" and all of its check history will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete service"
        requireText={service.name}
        loading={deleteService.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-semibold mt-1 font-mono tabular-nums">{value}</p>
    </Card>
  );
}

/** What the history row says instead of "HTTP 200" for the non-HTTP types. */
function checkLabel(type: string, c: Check): string {
  switch (type) {
    case 'http': return c.status_code ? `HTTP ${c.status_code}` : 'No response';
    case 'tcp': return c.status === 'down' ? 'No connection' : 'Connected';
    case 'dns': return c.status === 'down' ? 'No answer' : 'Resolved';
    case 'ping': return c.status === 'down' ? 'No reply' : 'Reply';
    case 'heartbeat': return c.status === 'down' ? 'Missed' : 'On time';
    case 'group': return c.status === 'down' ? 'Children down' : c.status === 'degraded' ? 'Children degraded' : 'Children up';
    default: return c.status;
  }
}

function certDays(iso: string): string {
  const d = Math.floor((new Date(iso).getTime() - Date.now()) / 86400000);
  if (Number.isNaN(d)) return '';
  if (d < 0) return `expired ${-d}d ago`;
  return `${d}d left`;
}

function CopyButton({ text }: { text: string }) {
  const { addToast } = useToast();
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          addToast('success', 'Copied');
        } catch {
          addToast('error', 'Could not copy — select the text instead');
        }
      }}
    >
      Copy
    </Button>
  );
}

function Detail({ term, value, mono }: { term: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-gray-500 dark:text-gray-400">{term}</dt>
      <dd className={`font-medium ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</dd>
    </div>
  );
}
